import { createSelector } from 'reselect'
// constant imports
import { ADD_CALL } from 'constants/General'

const rootSelector = (state) => state.sessions.list
const mainSelector = (state) => state.sessions

export const activeIdSelector = createSelector(
  mainSelector,
  (state) => state.activeSessionIndex,
)

export const hasMultipleSessionsSelector = createSelector(
  rootSelector,
  (sessions) => sessions.filter(Boolean).length > 1,
)

export const outgoingSessionsSelector = createSelector(
  rootSelector,
  (sessions) =>
    sessions.filter(
      (session) => session && session.outgoing && session.connecting,
    ),
)

export const hasOutgoingSessionSelector = createSelector(
  outgoingSessionsSelector,
  (sessions) => !!sessions.length,
)

export const hasIncomingSessionSelector = createSelector(
  rootSelector,
  (sessions) =>
    !!sessions.filter(
      (session) =>
        session &&
        session.incoming &&
        !session.connected &&
        !session.transferring,
    ).length,
)

export const incomingConnectingSessionsSelector = createSelector(
  rootSelector,
  (sessions) =>
    sessions.filter(
      (session) =>
        session &&
        session.incoming &&
        !session.connected &&
        !session.transferring,
    ),
)

export const connectedSessionsSelector = createSelector(
  rootSelector,
  (sessions) =>
    sessions.filter(
      (session) => session && session.connected && !session.merged,
    ),
)

export const connectedMergedSessionsSelector = createSelector(
  rootSelector,
  (sessions) =>
    sessions.filter(
      (session) => session && session.connected && session.merged,
    ),
)

export const connectedMergedActiveSessionsSelector = createSelector(
  rootSelector,
  (sessions) =>
    sessions.filter(
      (session) =>
        session && session.connected && session.merged && !session.hold,
    ),
)

export const activeSessionIsMergedSelector = createSelector(
  rootSelector,
  activeIdSelector,
  (sessions, index) =>
    index > -1 && sessions[index] && !!sessions[index].merged,
)

export const hasConnectedSessionSelector = createSelector(
  rootSelector,
  (sessions) =>
    !!sessions.filter((session) => session && session.connected).length,
)

export const hasConnectingSessionSelector = createSelector(
  rootSelector,
  (sessions) =>
    !!sessions.filter((session) => session && session.connecting).length,
)

export const hasActiveSessionSelector = createSelector(
  rootSelector,
  (sessions) =>
    !!sessions
      .filter(Boolean)
      .filter((session) => session.connecting || session.connected).length,
)

export const activeSessionSelector = createSelector(
  rootSelector,
  activeIdSelector,
  (list, id) => ({ session: list[id], activeId: id }),
)

export const actionSelector = createSelector(
  mainSelector,
  (state) => state.action,
)

export const isAddCallActionSelector = createSelector(
  actionSelector,
  (action) => action === ADD_CALL,
)

export const isNewCallActionSelector = createSelector(
  actionSelector,
  (action) => !action || action === ADD_CALL,
)

export const mergedCallsStateSelector = createSelector(
  mainSelector,
  (state) => ({
    mute: state.merged.mute,
    hold: state.merged.hold,
    tracksAreMixed: state.merged.tracksAreMixed,
    needToMixWithDelay: state.merged.needToMixWithDelay,
  }),
)

export const isSilencedSelector = createSelector(
  mainSelector,
  (sessions) => sessions.silenced,
)

export const hasMediaConnectedActiveSessionsSelector = createSelector(
  rootSelector,
  (list) =>
    list.filter((session) => session && session.mediaConnected).length > 0,
)

export const hasMediaConnectedCurrentActiveSessionsSelector = createSelector(
  rootSelector,
  // Current session is always the last one
  (list) => !!list?.length && list[list.length - 1]?.mediaConnected,
)

export const isPlayLocalRingToneActiveSessionsSelector = createSelector(
  rootSelector,
  (list) =>
    list.filter((session) => session && session.isPlayLocalRingTone).length > 0,
)

// RCON
// calculate if there is an active session
// Requirements:
// 1. session is connected
// 2. session is not on hold, transferring, parking
export const connectedActiveSessionsSelector = createSelector(
  rootSelector,
  (list) =>
    list.filter(
      (session) =>
        session &&
        session.connected &&
        !(session.hold || session.transferring || session.merged),
    ),
)

export const notHoldSessionsSelector = createSelector(
  rootSelector,
  (sessions) =>
    sessions.filter(
      (session) =>
        session && session.connected && !session.session.isOnHold().local,
    ),
)
